import React, { useState, useEffect } from 'react';
import { Col, Row, Empty, Button } from 'antd';
import { DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { TTripTaskRes } from 'api/swagger';
import { useGetTripTasks } from 'api/trip-task/use-get-trip-tasks';
import { useTripTaskSequence } from 'api/trip-task/use-update-trip-task-sequence';
import { useGetCargoTrips } from 'api/trips/use-get-cargo-trips';
import { useGetManyWarehouses } from 'api/warehouses/use-get-many-warehouses';
import { CardUi } from 'components/common/card-ui';
import { TripMapContainer } from 'components/map/map-container';
import { TripTaskItem } from 'components/trips/trip-tasks/components/trip-task-item/trip-task-item';
import type { ITripTasksProps } from 'components/trips/trip-tasks/type';
import styles from './trip-tasks.module.scss';
import { getMergeTasks } from './utils';

export const TripTasks: React.FC<ITripTasksProps> = ({ trip }) => {
  const { data: _tripTasks, isLoading: isTripTasksLoading } = useGetTripTasks({ tripId: trip.id });
  const { data: cargoTrips, isLoading: isCargoTripsLoading } = useGetCargoTrips(trip.id);

  const { data: warehouses } = useGetManyWarehouses();

  const [tripTasks, setTripTasks] = useState<TTripTaskRes[]>([]);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    // используем state в место кеша react-query, чтобы избежать кривой анимации в драг энд дроп
    setTripTasks(_tripTasks || []);
  }, [_tripTasks]);

  const { mutateAsync: updateTripTaskSequence } = useTripTaskSequence(trip.id);

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    const activeTripTaskIdx = tripTasks?.findIndex((tripTask) => tripTask.id === active?.id);
    const overTripTaskIdx = tripTasks?.findIndex((tripTask) => tripTask.id === over?.id);

    const activeTripTask = tripTasks?.find((tripTask) => tripTask.id === active?.id);
    const overTripTask = tripTasks?.find((tripTask) => tripTask.id === over?.id);

    if (!tripTasks || !activeTripTask || !overTripTask) {
      return;
    }

    const newSequence: number = overTripTask.sequence;

    const prevTripTasks = [...tripTasks];

    const newResult = arrayMove(tripTasks, activeTripTaskIdx!, overTripTaskIdx!);

    setTripTasks(newResult);

    try {
      await updateTripTaskSequence({
        tripTaskId: activeTripTask.id,
        sequence: newSequence,
      });
    } catch (error) {
      setTripTasks(prevTripTasks);
    }
  };

  const isDraggable = trip.status !== 'done';

  if (isTripTasksLoading || isCargoTripsLoading) {
    return null;
  }

  return (
    <CardUi className={styles.cardContainer}>
      <Row>
        <Col span={17}>
          {showMap ? (
            <TripMapContainer
              tripTasks={tripTasks}
              cargoTrips={cargoTrips}
              transports={[trip.truck]}
              warehouses={warehouses || []}
              isBig
            />
          ) : (
            <div className={styles.mapPlaceholder}>
              <Button onClick={() => setShowMap(true)}>Show map</Button>
            </div>
          )}
        </Col>
        <Col span={7} className={styles.tripList}>
          {!tripTasks?.length ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No tasks here' />
          ) : (
            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
              <SortableContext items={tripTasks.map((tripTask) => tripTask.id)} strategy={verticalListSortingStrategy}>
                {tripTasks?.map((tripTask) => (
                  <TripTaskItem
                    key={tripTask.id}
                    trip={trip}
                    tripTask={tripTask}
                    mergeTasks={getMergeTasks(tripTask, tripTasks)}
                    isDraggable={isDraggable}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Col>
      </Row>
    </CardUi>
  );
};
